(() => {
  const closeAll = (root) => {
    root.querySelectorAll('[data-flashark-menu-item]').forEach((item) => item.classList.remove('is-active'));
    root.querySelectorAll('[data-flashark-mega]').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    root.querySelectorAll('[data-flashark-menu-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  };

  const openPanel = (root, index) => {
    closeAll(root);
    const item = root.querySelector(`[data-flashark-menu-item][data-menu-index="${index}"]`);
    const panel = root.querySelector(`[data-flashark-mega][data-menu-index="${index}"]`);
    const trigger = item && item.querySelector('[data-flashark-menu-trigger]');
    if (!item || !panel) return;
    item.classList.add('is-active');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (window.matchMedia('(max-width: 999px)').matches) {
      root.classList.remove('is-categories-open');
      const categoryToggle = root.querySelector('[data-flashark-category-toggle]');
      if (categoryToggle) categoryToggle.setAttribute('aria-expanded', 'false');
      const firstSubmenu = panel.querySelector('[data-flashark-submenu]');
      if (firstSubmenu) activateSubmenu(firstSubmenu);
    }
    root.classList.add('is-menu-open');
  };

  const activateSubmenu = (button) => {
    const mega = button.closest('[data-flashark-mega]');
    if (!mega || button.classList.contains('is-active')) return;
    mega.querySelectorAll('[data-flashark-submenu], [data-flashark-collection]').forEach((element) => element.classList.remove('is-active'));
    button.classList.add('is-active');
    const collection = mega.querySelector(`[data-flashark-collection="${button.dataset.flasharkSubmenu}"]`);
    if (collection) collection.classList.add('is-active');
  };

  const setMobileNavigation = (root, isOpen) => {
    root.classList.toggle('is-mobile-nav-open', isOpen);
    const toggle = root.querySelector('[data-flashark-mobile-menu-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.setAttribute('aria-label', isOpen ? 'Close navigation' : 'Open navigation');
    }
    if (!isOpen) {
      closeAll(root);
      root.classList.remove('is-menu-open', 'is-categories-open');
    }
  };

  const setSearchPanel = (root, isOpen) => {
    const toggle = root.querySelector('[data-flashark-search-toggle]');
    const panel = root.querySelector('[data-flashark-search-panel]');
    if (!panel) return;

    root.classList.toggle('is-search-open', isOpen);
    panel.setAttribute('aria-hidden', String(!isOpen));
    if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      window.setTimeout(() => {
        const input = panel.querySelector('[data-flashark-search-input]');
        if (input) input.focus();
      }, 0);
    }
  };

  const initFlasharkCartDrawer = (root) => {
    const cartToggle = root.querySelector('.flashark-header__cart[data-flashark-cart-toggle]');
    const miniCart = document.getElementById('mini-cart');
    if (!cartToggle || !miniCart) return;

    // The header is a stacking context. Keep the drawer as a direct child of
    // <body> so the page backdrop can dim the site without ever sitting above
    // the cart panel itself.
    if (miniCart.parentElement !== document.body) {
      document.body.appendChild(miniCart);
    }

    const updateMobileCartDrawerPosition = () => {
      if (!window.matchMedia('(max-width: 640px)').matches) {
        document.documentElement.style.removeProperty('--flashark-cart-drawer-top');
        return;
      }

      const header = root.querySelector('.flashark-header');
      if (!header) return;
      const announcement = document.querySelector('.shopify-section--announcement-bar');
      const headerRect = header.getBoundingClientRect();
      const hiddenAnnouncementHeight = announcement?.classList.contains('is-announcement-hidden')
        ? announcement.offsetHeight
        : 0;
      // The announcement bar hides with transform, so subtract its retained
      // layout height when calculating the visual bottom of the mobile header.
      const drawerTop = Math.max(0, Math.round(headerRect.bottom - hiddenAnnouncementHeight));
      document.documentElement.style.setProperty('--flashark-cart-drawer-top', `${drawerTop}px`);
    };

    let mobileDrawerPositionTimer;
    const scheduleMobileCartDrawerPosition = () => {
      window.requestAnimationFrame(updateMobileCartDrawerPosition);
      window.clearTimeout(mobileDrawerPositionTimer);
      mobileDrawerPositionTimer = window.setTimeout(updateMobileCartDrawerPosition, 260);
    };

    updateMobileCartDrawerPosition();
    window.addEventListener('scroll', scheduleMobileCartDrawerPosition, { passive: true });
    window.addEventListener('resize', scheduleMobileCartDrawerPosition);
    const announcement = document.querySelector('.shopify-section--announcement-bar');
    if (announcement) {
      new MutationObserver(scheduleMobileCartDrawerPosition).observe(announcement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    const syncState = () => {
      const isOpen = miniCart.getAttribute('aria-hidden') === 'false';
      cartToggle.setAttribute('aria-expanded', String(isOpen));
      document.body.classList.toggle('flashark-cart-drawer-open', isOpen);
    };
    const setDrawerState = (isOpen) => {
      miniCart.setAttribute('aria-hidden', String(!isOpen));
      document.body.classList.toggle('no-mobile-scroll', isOpen);
      syncState();
    };
    const openDrawer = () => {
      updateMobileCartDrawerPosition();
      setDrawerState(true);
    };
    const closeDrawer = () => setDrawerState(false);
    const cartUrl = () => window.routes?.cartUrl || '/cart';
    const cartUpdateUrl = () => window.routes?.cartUpdateUrl || '/cart/update';
    const cartChangeUrl = () => window.routes?.cartChangeUrl || '/cart/change';
    const endpointWithJson = (url) => url.endsWith('.js') ? url : `${url}.js`;

    const setCouponStatus = (message = '', state = '') => {
      const coupon = miniCart.querySelector('.mini-cart__coupon');
      if (!coupon) return;
      let status = coupon.querySelector('[data-flashark-coupon-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'mini-cart__coupon-status';
        status.dataset.flasharkCouponStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        coupon.append(status);
      }
      status.textContent = message;
      status.hidden = !message;
      status.dataset.state = state;
    };

    const cartIncludesDiscount = (cart, code) => {
      const normalizedCode = code.trim().toLowerCase();
      const applications = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap((item) => item.line_level_discount_allocations || [])
      ];
      return applications.some((application) => String(application.title || '').trim().toLowerCase() === normalizedCode);
    };

    const renderedCartIncludesDiscount = (code) => {
      const normalizedCode = code.trim().toLowerCase();
      return Array.from(miniCart.querySelectorAll('.mini-cart__recap-price-line--highlight'))
        .some((line) => line.textContent.toLowerCase().includes(normalizedCode));
    };

    const getRenderedDiscountState = () => ({
      discounts: Array.from(miniCart.querySelectorAll('.mini-cart__recap-price-line--highlight'))
        .map((line) => line.textContent.trim()),
      amountSaved: miniCart.querySelector('.mini-cart__amount-saved')?.textContent.trim() || '',
      total: Array.from(miniCart.querySelectorAll('.mini-cart__recap-price-line:not(.mini-cart__recap-price-line--highlight)'))
        .map((line) => line.textContent.trim())
        .join('|')
    });

    // The cart section can be re-rendered by Shopify after an item update. Keep
    // the Flashark title/close bar present even while an older section response
    // is still cached by the theme editor.
    const ensureCartHeader = () => {
      if (miniCart.querySelector('.mini-cart__header')) return;
      const header = document.createElement('header');
      header.className = 'mini-cart__header';

      const title = document.createElement('span');
      title.className = 'mini-cart__title';
      title.textContent = 'Your cart';

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'mini-cart__close';
      close.dataset.flasharkCartClose = '';
      close.setAttribute('aria-label', 'Close cart');
      close.innerHTML = '&times;';

      header.append(title, close);
      const triangle = miniCart.querySelector('.icon--nav-triangle-borderless');
      miniCart.insertBefore(header, triangle?.nextSibling || miniCart.firstChild);
    };

    ensureCartHeader();

    const refreshCart = async ({ open = false } = {}) => {
      try {
        const refreshUrl = new URL(cartUrl(), window.location.origin);
        refreshUrl.searchParams.set('section_id', 'mini-cart');
        // Shopify's section endpoint can otherwise return an older cart render.
        refreshUrl.searchParams.set('_flashark_cart', Date.now().toString());
        const response = await fetch(refreshUrl.toString(), {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);

        const wrapper = document.createElement('div');
        wrapper.innerHTML = await response.text();
        const updatedCart = wrapper.querySelector('#mini-cart');
        if (!updatedCart) throw new Error('Mini cart section was not returned');

        miniCart.innerHTML = updatedCart.innerHTML;
        miniCart.dataset.itemCount = updatedCart.dataset.itemCount || '0';
        ensureCartHeader();
        document.querySelectorAll('.flashark-header__cart-count, .header__cart-count').forEach((count) => {
          count.textContent = miniCart.dataset.itemCount;
        });
        if (open) openDrawer();
      } catch (error) {
        console.error('Flashark cart drawer:', error);
      }
    };

    cartToggle.addEventListener('click', async (event) => {
      event.preventDefault();
      if (miniCart.getAttribute('aria-hidden') === 'false') closeDrawer();
      else await refreshCart({ open: true });
    });

    new MutationObserver(syncState).observe(miniCart, { attributes: true, attributeFilter: ['aria-hidden'] });
    document.addEventListener('click', (event) => {
      const close = event.target.closest('[data-flashark-cart-close]');
      if (!close) return;
      event.preventDefault();
      closeDrawer();
    });

    document.addEventListener('click', async (event) => {
      const quantityTrigger = event.target.closest('#mini-cart [data-action="increase-quantity"], #mini-cart [data-action="decrease-quantity"]');
      if (!quantityTrigger) return;
      event.preventDefault();
      const line = Number.parseInt(quantityTrigger.dataset.line, 10);
      const quantity = Number.parseInt(quantityTrigger.dataset.quantity, 10);
      if (!Number.isInteger(line) || !Number.isInteger(quantity) || quantity < 0) return;

      try {
        const response = await fetch(endpointWithJson(cartChangeUrl()), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        await refreshCart({ open: true });
      } catch (error) {
        console.error('Flashark cart update:', error);
      }
    });

    document.addEventListener('change', async (event) => {
      const quantityInput = event.target.closest('#mini-cart .quantity-selector__value');
      if (!quantityInput) return;
      const line = Number.parseInt(quantityInput.dataset.line, 10);
      const quantity = Number.parseInt(quantityInput.value, 10);
      if (!Number.isInteger(line) || !Number.isInteger(quantity) || quantity < 0) return;
      try {
        const response = await fetch(endpointWithJson(cartChangeUrl()), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        await refreshCart({ open: true });
      } catch (error) {
        console.error('Flashark cart update:', error);
      }
    });

    document.addEventListener('click', (event) => {
      if (miniCart.getAttribute('aria-hidden') !== 'false') return;
      if (miniCart.contains(event.target) || cartToggle.contains(event.target)) return;
      closeDrawer();
    });

    document.addEventListener('product:added', () => {
      refreshCart({ open: true });
    });

    // Used by the custom homepage product cards after their independent Ajax
    // add-to-cart request succeeds. Keeping this separate prevents the
    // legacy theme cart controller from rendering the drawer a second time.
    document.addEventListener('flashark:cart-updated', () => {
      refreshCart({ open: true });
    });

    document.addEventListener('cart:refresh', () => {
      refreshCart({ open: miniCart.getAttribute('aria-hidden') === 'false' });
    });

    document.addEventListener('click', (event) => {
      const apply = event.target.closest('[data-flashark-coupon-apply]');
      if (!apply) return;
      const input = miniCart.querySelector('[data-flashark-cart-discount]');
      const code = input?.value.trim();
      if (!code) {
        setCouponStatus('Enter a discount code.', 'error');
        input?.focus();
        return;
      }

      apply.disabled = true;
      apply.setAttribute('aria-busy', 'true');
      setCouponStatus('Applying code…', 'loading');
      const previousDiscountState = getRenderedDiscountState();

      fetch(endpointWithJson(cartUpdateUrl()), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: code })
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Discount update failed: ${response.status}`);
        const cart = await response.json();
        await refreshCart({ open: true });
        // Some theme sessions return the updated discount only in the rendered
        // mini-cart section, not in cart/update.js. A switched code can also
        // reuse a different title, so an actual discount/total change is valid.
        const nextDiscountState = getRenderedDiscountState();
        const discountStateChanged = JSON.stringify(previousDiscountState) !== JSON.stringify(nextDiscountState);
        const isApplied = cartIncludesDiscount(cart, code)
          || renderedCartIncludesDiscount(code)
          || (discountStateChanged && (nextDiscountState.discounts.length > 0 || nextDiscountState.amountSaved !== ''));
        setCouponStatus(
          isApplied ? `Code ${code.toUpperCase()} applied.` : 'This code is not applicable to the current cart.',
          isApplied ? 'success' : 'error'
        );
      }).catch((error) => {
        console.error('Flashark cart discount:', error);
        setCouponStatus('Unable to apply the code. Please try again.', 'error');
      }).finally(() => {
        const refreshedApply = miniCart.querySelector('[data-flashark-coupon-apply]');
        if (refreshedApply) {
          refreshedApply.disabled = false;
          refreshedApply.removeAttribute('aria-busy');
        }
      });
    });
  };

  // The custom Flashark header uses the theme's search markup, but it does not
  // live inside the original HeaderSection. Reconnect it to the same predictive
  // search endpoint used by the theme's native search bar.
  const initPredictiveSearch = (root) => {
    const form = root.querySelector('[data-flashark-search-form]');
    const input = root.querySelector('[data-flashark-search-input]');
    const results = root.querySelector('[data-flashark-search-results]');
    const resultsInner = root.querySelector('[data-flashark-search-results-inner]');
    const menu = root.querySelector('[data-flashark-search-menu]');
    const clear = root.querySelector('[data-flashark-search-clear]');
    const filter = root.querySelector('[data-flashark-search-filter]');
    if (!form || !input || !results || !resultsInner) return;

    let timer;
    let requestController;
    const supportsPredictiveApi = () => {
      try {
        return Boolean(JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch);
      } catch (_) {
        return true;
      }
    };

    const setEmptyState = () => {
      requestController?.abort();
      resultsInner.replaceChildren();
      results.setAttribute('aria-hidden', 'true');
      menu?.setAttribute('aria-hidden', 'false');
      form.classList.remove('is-expanded', 'is-loading');
      input.classList.remove('is-filled');
    };

    const renderResults = async () => {
      const term = input.value.trim();
      if (!term) {
        setEmptyState();
        return;
      }

      requestController?.abort();
      requestController = new AbortController();
      const requestedTerm = term;
      const productType = filter?.value || '';
      const query = productType ? `product_type:${productType} AND ${term}` : term;
      const params = new URLSearchParams({
        section_id: 'predictive-search',
        q: query,
        'resources[limit]': '3',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor'
      });
      const rootUrl = window.Shopify?.routes?.root || '/';
      const searchPath = supportsPredictiveApi() ? '/search/suggest' : '/search';
      const url = `${rootUrl.replace(/\/$/, '')}${searchPath}?${params.toString()}`;

      menu?.setAttribute('aria-hidden', 'true');
      results.setAttribute('aria-hidden', 'false');
      form.classList.add('is-expanded', 'is-loading');
      input.classList.add('is-filled');

      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: requestController.signal });
        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
        const documentFragment = document.createElement('div');
        documentFragment.innerHTML = await response.text();
        if (input.value.trim() !== requestedTerm) return;
        const searchAjax = documentFragment.querySelector('.search-ajax');
        resultsInner.innerHTML = searchAjax?.innerHTML || '<div class="search-bar__empty-state"><p class="heading h4">No results found</p></div>';
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Flashark predictive search:', error);
        if (input.value.trim() === requestedTerm) {
          resultsInner.innerHTML = '<div class="search-bar__empty-state"><p class="heading h4">Unable to load suggestions. Please try again.</p></div>';
        }
      } finally {
        if (input.value.trim() === requestedTerm) form.classList.remove('is-loading');
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(renderResults, 250);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim()) renderResults();
      else menu?.setAttribute('aria-hidden', 'false');
    });

    clear?.addEventListener('click', () => {
      input.value = '';
      setEmptyState();
      input.focus();
    });

    filter?.addEventListener('change', () => {
      const activeLabel = filter.closest('.search-bar__filter')?.querySelector('.search-bar__filter-active');
      if (activeLabel) activeLabel.textContent = filter.options[filter.selectedIndex]?.textContent || '';
      if (input.value.trim()) renderResults();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-flashark-navigation]').forEach((root) => {
      initFlasharkCartDrawer(root);
      initPredictiveSearch(root);
      const mobileToggle = root.querySelector('[data-flashark-mobile-menu-toggle]');
      if (mobileToggle) {
        mobileToggle.addEventListener('click', () => {
          const isOpening = !root.classList.contains('is-mobile-nav-open');
          setMobileNavigation(root, isOpening);
          if (isOpening) {
            const firstTrigger = root.querySelector('[data-flashark-menu-trigger]');
            const firstItem = firstTrigger && firstTrigger.closest('[data-flashark-menu-item]');
            if (firstItem) openPanel(root, firstItem.dataset.menuIndex);
          }
        });
      }

      const searchToggle = root.querySelector('[data-flashark-search-toggle]');
      if (searchToggle) {
        searchToggle.addEventListener('click', () => {
          setSearchPanel(root, !root.classList.contains('is-search-open'));
        });
      }

      root.querySelectorAll('[data-flashark-menu-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const item = trigger.closest('[data-flashark-menu-item]');
          const index = item.dataset.menuIndex;
          const panel = root.querySelector(`[data-flashark-mega][data-menu-index="${index}"]`);
          if (panel.classList.contains('is-open')) {
            closeAll(root);
            root.classList.remove('is-menu-open');
          } else openPanel(root, index);
        });
      });

      root.querySelectorAll('[data-flashark-menu-item]').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (window.matchMedia('(min-width: 1000px)').matches && item.querySelector('[data-flashark-menu-trigger]')) openPanel(root, item.dataset.menuIndex);
        });
      });
      root.addEventListener('mouseleave', () => {
        if (window.matchMedia('(min-width: 1000px)').matches) {
          closeAll(root);
          root.classList.remove('is-menu-open');
        }
      });

      root.querySelectorAll('[data-flashark-submenu]').forEach((button) => {
        button.addEventListener('click', () => activateSubmenu(button));
        button.addEventListener('mouseenter', () => {
          if (window.matchMedia('(min-width: 1000px)').matches) activateSubmenu(button);
        });
      });

      const more = root.querySelector('[data-flashark-category-toggle]');
      if (more) more.addEventListener('click', () => {
        root.classList.toggle('is-categories-open');
        more.setAttribute('aria-expanded', String(root.classList.contains('is-categories-open')));
      });
      root.querySelectorAll('[data-flashark-mobile-top]').forEach((button) => button.addEventListener('click', () => openPanel(root, button.dataset.flasharkMobileTop)));
      root.querySelectorAll('[data-flashark-menu-close]').forEach((button) => button.addEventListener('click', () => {
        closeAll(root);
        setMobileNavigation(root, false);
      }));

      document.addEventListener('click', (event) => {
        if (root.classList.contains('is-search-open') && !root.contains(event.target)) setSearchPanel(root, false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-flashark-navigation]').forEach((root) => {
        closeAll(root);
        setMobileNavigation(root, false);
        setSearchPanel(root, false);
      });
    });
  });
})();
